import { useEffect, useRef, useState } from "react";
import { tokenManager } from "lib/tokenManager";
import { trpc } from "lib/trpc";

// Captured at module-eval time: TanStack Router normalizes the URL (dropping
// the fragment) when it boots, so reading location.hash inside the component
// is already too late — bundle evaluation runs first.
const initialExchangeCode = new URLSearchParams(window.location.hash.slice(1)).get("code");

/**
 * PRTL-1348 masquerade landing: redeems the single-use exchange code from
 * the URL FRAGMENT (never sent to the server in a request line or referrer)
 * for a 1-hour read-only token, stored in sessionStorage only.
 */
export const Masquerade = () => {
    const [error, setError] = useState("");
    const attempted = useRef(false);
    const exchangeMutation = trpc.auth.exchangeMasqueradeCode.useMutation({
        onSuccess: ({ token }) => {
            tokenManager.setMasquerade(token);
            window.location.replace("/");
        },
        onError: (e) => setError(e.message || "This masquerade link is invalid or expired."),
    });

    useEffect(() => {
        if (attempted.current) return;
        attempted.current = true;
        const code =
            initialExchangeCode ?? new URLSearchParams(window.location.hash.slice(1)).get("code");
        // Scrub the code from the address bar/history immediately.
        window.history.replaceState(null, "", "/masquerade");
        if (!code) {
            setError("Missing masquerade code.");
            return;
        }
        exchangeMutation.mutate({ code });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="flex min-h-screen w-full items-center justify-center bg-linear-to-br from-[#e8f4f8] to-[#d4e9f0] p-6">
            <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-sm">
                {error ? (
                    <>
                        <h1 className="mb-2 text-lg font-semibold text-red-700">
                            Masquerade failed
                        </h1>
                        <p className="text-sm text-gray-600">{error}</p>
                        <p className="mt-4 text-sm text-gray-500">
                            Generate a new link from the admin panel (links are single-use and
                            expire after 60 seconds).
                        </p>
                    </>
                ) : (
                    <p className="text-sm text-gray-600">Starting masquerade session…</p>
                )}
            </div>
        </div>
    );
};
